import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import { Form, Select, FormLayout, Layout, Page, Card, TextField, Checkbox, BlockStack, InlineGrid, Badge } from '@shopify/polaris';
import { TheSaveBar } from '../../shared/components/theSaveBar';
import { updateMyData } from '../../shared/util/updateLines';
import { handleSave, handleDiscard, useUrl } from '../../shared/util/hanldesave';
import { alphabetize, getRequest, simplePostRequest } from '../../shared/components/functions.jsx';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import { validateLines, validateSchema } from '../../helpers/schemas/schemasHelpers.js';
import { InquiryToPurchase } from '../../shared/components/transformers';
import DocumentCard from '../../shared/components/cards/DocumentCard';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import CompanySelect from '../../shared/components/companySelect';
import ProjectSelect from '../../shared/components/projectSelect.jsx';
import LinesTotalCard from '../../shared/components/cards/LinesTotalsCard.jsx';
import LinesTable from '../../shared/components/linesTable';
import { getPageTitle, TabTitle } from '../../shell/helmet';
import { getSchema } from '../../shared/formSchemas';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import OwnerSelect from '../../shared/components/ownerSelect.jsx';
import EventTimeLine from '../../shared/components/events/eventTimeLine';
import InquiryLifecycleCard from './components/InquiryLifecycleCard.jsx';
import MakePurchaseOrderModal from './components/MakePurchaseOrderModal.jsx';
import { usePartDocumentsPrompt } from '../../shared/components/modals/partDocumentsPromptModal.jsx';
import { deriveInquiryStatusId, getInquiryStatusLabel, getInquiryStatusTone } from '../../shared/inquiryStatus.js';

// An inquiry PDF can be generated without the end customer. The choice is not stored on the inquiry itself but read back
// from the description of the generated system document (see server/routes/docgen.js)
const CUSTOMER_HIDDEN_MARKER = 'customer hidden';

export default function InquiryPage() {
  let { id } = useParams();
  const pagination = useRecordPagination('inquiries', id, 'inquiry');
  let url = useUrl();
  const navigate = useNavigate();
  const docTypeID = 14;
  const docType = 'price_request';
  const [savingResult, setSavingResult] = useState(false);
  const [inquiry, setInquiry] = useState({});
  const [initialInquiry, setInitialInquiry] = useState({});
  const [lines, setLines] = useState([]);
  const [initialLines, setInitialLines] = useState([]);
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false); //eslint-disable-line
  const [isDirty, setIsDirty] = useState(false);
  const [linesDirty, setLinesDirty] = useState(false);
  const [company, setCompany] = useState({});
  const [customer, setCustomer] = useState({});
  const [deliveryCompany, setDeliveryCompany] = useState({});
  const [isBlurred, setIsBlurred] = useState(true);
  const [documentInProgress, setDocumentInProgress] = useState(false);
  const [hideCustomer, setHideCustomer] = useState(false);
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [timelineRefresh, setTimelineRefresh] = useState(0);
  const [documentCardKey, setDocumentCardKey] = useState(0);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalLoading, setOrderModalLoading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const countries = useLookupStore((state) => state.countries);
  const languages = useLookupStore((state) => state.languages);
  const { user } = useAuthStore();
  const pageTitle = getPageTitle(inquiry.Prijsaanvraagnummer, 'Inquiry');
  const inquirySchema = getSchema('inquiry', url, null, user);
  // Draft / Sent / Quotation received / Ordered, derived on the server (see InquiryLifecycleCard)
  const statusId = deriveInquiryStatusId(inquiry);
  const statusLabel = getInquiryStatusLabel(statusId, inquiryStatuses);
  // parts with documents (drawings, ...) put on a line: offer to copy those files into this inquiry
  const partDocuments = usePartDocumentsPrompt({
    resourceName: 'inquiries',
    resourceId: id,
    docTypeId: docTypeID,
    label: 'inquiry',
    onCopied: () => setDocumentCardKey((key) => key + 1)
  });

  const lineInterface = {
    lineIsDirty: false,
    PrijsaanvraagLijnID: null,
    PrijsaanvraagID: id,
    produktnr: null,
    produkthulpnr: null,
    tekstlijn: null,
    VP: null,
    aantal: 1,
    BTW: company.LandID === 300 ? 21 : null,
    MuntVP: null,
    AP: null,
    ProductID: null,
    ProductName: null,
    ProductNameNL: '',
    ProductNameFR: '',
    Productref: '',
    SupplierID: null,
    UnitsOnOrder: null,
    'Eenheids verkoopprijs': null,
    'Munt verkoopprijs': null,
    'Datum  bijw_ VP': '',
    ReorderLevel: 0,
    Discontinued: false,
    Eenheidsaankoopprijs: 0,
    'Munt aankoopprijs': null,
    'Datum bijw_AP': '',
    Omschrijving: '',
    Eenheid: null,
    ProductNameDE: '',
    ProductNameUK: '',
    EenheidID: null,
    EenheidAantal: null,
    ParentProductID: null,
    HasDetails: false,
    HSCode: null,
    Kg: null,
    OnderdelenTypeID: null
  };

  const handleChange = async (value, id) => {
    if (id === 'RefKlantDatum' && (!value || value === '')) value = null;

    // If a project is selected, automatically set the customer and update the reference to PROJECT-XXXXXX format
    if (id === 'ProjectID') {
      try {
        if (value) {
          // Project is selected - fetch project data and update reference
          const projectData = await getRequest(`/api/projects/${value}`);
          if (projectData && projectData[0] && projectData[0].Projectnummer) {
            const newReference = `PROJECT-${projectData[0].Projectnummer}`;

            // Check if current reference is empty or still follows the old project format
            const shouldUpdateReference = !inquiry.RefKlant || inquiry.RefKlant.startsWith('PROJECT-') || inquiry.RefKlant === '';

            setInquiry((inquiry) => ({
              ...inquiry,
              [id]: value,
              KlantID: projectData[0].firnr || inquiry.KlantID, // Set customer from project
              ...(shouldUpdateReference && { RefKlant: newReference })
            }));
            setIsDirty(true);
            return;
          }
        } else {
          // Project is cleared - clear the reference if it was in PROJECT-XXXXXX format
          if (inquiry.RefKlant && inquiry.RefKlant.startsWith('PROJECT-')) {
            setInquiry((inquiry) => ({
              ...inquiry,
              [id]: value,
              RefKlant: ''
            }));
            setIsDirty(true);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
      }
    }

    setInquiry((inquiry) => ({ ...inquiry, [id]: value }), setIsDirty(true));
  };

  useEffect(() => {
    async function fetchData() {
      if (id === 'new') {
        setInquiry(inquirySchema.default());
        setHideCustomer(false);
      } else {
        const response = await getRequest(`/api/inquiries/${id}`);
        if (!response) return setInquiry(inquirySchema.default());
        setInquiry(response);
        setInitialInquiry(response);

        const response2 = await getRequest(`/api/inquiries/${id}/lines`);
        setLines(response2);
        setInitialLines(response2);

        const documents = await getRequest(`/api/documents/id/typeid?id=${id}&typeID=${docTypeID}&sysDoc=1`);
        const description = Array.isArray(documents) && documents.length > 0 ? String(documents[0].Omschrijving || '') : '';
        setHideCustomer(description.toLowerCase().includes(CUSTOMER_HIDDEN_MARKER));
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    async function fetchLookups() {
      const statuses = await getRequest('/api/inquiries/statuses');
      if (Array.isArray(statuses)) setInquiryStatuses(statuses);
    }
    fetchLookups();
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (inquiry.LeverancierID) {
        const response3 = await getRequest(`/api/companies/${inquiry.LeverancierID}`);
        setCompany(response3);
      }
      if (inquiry.KlantID) {
        const response4 = await getRequest(`/api/companies/${inquiry.KlantID}`);
        setCustomer(response4);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiry.LeverancierID, inquiry.KlantID]);

  useEffect(() => {
    async function fetchDeliveryCompany() {
      setDeliveryCompany(inquiry.LeveringKlantID ? (await getRequest(`/api/companies/${inquiry.LeveringKlantID}`)) || {} : {});
    }
    fetchDeliveryCompany();
  }, [inquiry.LeveringKlantID]);

  const generateDocument = async () => {
    const document = {
      addressee: {
        FIRNR: inquiry.LeverancierID,
        name: company.NAAM,
        number: company.Klantnummer,
        address: company.STRAAT_NR,
        city: company.WOONPL,
        zip: company.POSTNR,
        country: countries.find((c) => c.LandId === company.LandID)?.Land,
        countryId: company.LandID,
        vat: company.BTW,
        postal_code: company.postkode
      },
      eu: false,
      language: inquiry.TaalID ? languages.find((l) => l.TaalID === parseInt(inquiry.TaalID))?.Taal : 'N',
      items: lines,
      type: docType, //{'invoice','service_report','delivery_note','quotation','service_report','order_confirmation','maintenance_contract','order','price_request','reminder','project','credit_note','proforma','invoice_in','credit_note_in','letter','fax','info_schuilenburg','part','customer'}
      id: inquiry.PrijsaanvraagID,
      typeID: docTypeID,
      number: inquiry.Prijsaanvraagnummer,
      reference: customer.NAAM,
      reference_date: inquiry.RefKlantDatum ? String(inquiry.RefKlantDatum).substring(0, 10) : '',
      date: String(inquiry.Datum).substring(0, 10),
      payment_condition: null,
      machine_type: inquiry.MachineType,
      machine_number: inquiry.MachineNummer,
      hideCustomer,
      // optional delivery address (a customer or another supplier), printed on the PDF so the supplier knows where to deliver
      deliveryAddressee:
        inquiry.LeveringKlantID && String(deliveryCompany.FIRNR) === String(inquiry.LeveringKlantID)
          ? {
              name: deliveryCompany.NAAM,
              number: deliveryCompany.Klantnummer,
              address: deliveryCompany.STRAAT_NR,
              city: deliveryCompany.WOONPL,
              zip: deliveryCompany.POSTNR,
              country: countries.find((c) => c.LandId === deliveryCompany.LandID)?.Land,
              countryId: deliveryCompany.LandID
            }
          : null
    };

    //check if it is dirty if not tell user to save first
    setDocumentInProgress(true);
    const res = await simplePostRequest(`/api/docgen`, document);
    if (res.ok) setDocumentInProgress(false);
  };

  // with uploaded files (drawings, specs, the supplier quotation) the user picks which ones go along to the order
  const makePurchaseOrder = async () => {
    setOrderModalLoading(true);
    setOrderModalOpen(true);
    const documents = await getRequest(`/api/documents/id/typeid?id=${id}&typeID=${docTypeID}`);
    const uploaded = Array.isArray(documents) ? documents.filter((doc) => !doc.Systeem) : [];
    setUploadedDocuments(uploaded);
    setOrderModalLoading(false);
    if (uploaded.length === 0) {
      setOrderModalOpen(false);
      InquiryToPurchase(inquiry, lines, company, lineInterface, navigate);
    }
  };

  // Lifecycle actions (mark as sent, quotation upload, ...) answer with the updated inquiry
  const handleInquiryUpdated = (updatedInquiry) => {
    if (!updatedInquiry) return;
    setInquiry(updatedInquiry);
    setInitialInquiry(updatedInquiry);
    setIsDirty(false);
    setTimelineRefresh((key) => key + 1);
  };

  async function saveForm() {
    const errorInSchema = await validateSchema(inquirySchema, inquiry);
    const errorInLines = await validateLines(lines);
    if (errorInLines) return alert(errorInLines);

    if (!documentInProgress) {
      if (!errorInSchema) {
        await handleSave(
          id,
          'inquiries',
          inquiry.PrijsaanvraagID,
          inquiry,
          setInquiry,
          setInitialInquiry,
          setToast,
          setToastError,
          isDirty,
          setIsDirty,
          linesDirty,
          lines,
          setLines,
          setInitialLines,
          setLinesDirty,
          setSavingResult,
          navigate
        );

        // auto generate pdf so latest changes are reflected in pdf
        if (id && id !== 'new') await generateDocument();
        setIsBlurred(false);
        setTimelineRefresh((key) => key + 1);
      } else {
        alert(errorInSchema);
      }
    }
  }

  return (
    <Page pagination={pagination} title={pageTitle} fullWidth>
      <TabTitle title={pageTitle} />
      <Toasts toast={toast} setToast={setToast} errorToast={toastError} setErrorToast={setToastError} toastContent="Inquiry saved" />
      <TheSaveBar
        isDirty={isDirty || linesDirty}
        savingResult={savingResult}
        handleDiscard={() => handleDiscard(initialInquiry, initialLines, setInquiry, setLines, setIsDirty, setLinesDirty)}
        handleSave={saveForm}
        disabled={documentInProgress || (!isBlurred && linesDirty)}
      />
      <Layout>
        <Layout.Section>
          <Card>
            <Form onSubmit={saveForm} autoComplete="off">
              <FormLayout>
                <FormLayout.Group>
                  <TextField id="Prijsaanvraagnummer" disabled label="Number" value={inquiry.Prijsaanvraagnummer ? String(inquiry.Prijsaanvraagnummer) : 'created automatically'} />
                  <TextField id="LeverancierID" label="Supplier" value={company.NAAM} disabled labelAction={{ content: 'open', onAction: () => navigate(`/companies/${inquiry.LeverancierID}`) }} />
                  <div>
                    <div style={{ marginBottom: '4px', fontSize: '12px', color: '#6d7175' }}>Status</div>
                    <Badge tone={getInquiryStatusTone(statusId)}>{statusLabel}</Badge>
                  </div>
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField id="Datum" maxLength={60} type="date" autoComplete="off" label="Date" value={String(inquiry.Datum).substring(0, 10)} onChange={handleChange} disabled={savingResult} />
                  <CompanySelect isClearable id="KlantID" label="Customer" value={inquiry.KlantID} onChange={handleChange} disabled={savingResult} />
                </FormLayout.Group>
                <CompanySelect
                  isClearable
                  id="LeveringKlantID"
                  label="Delivery address (optional, printed on the PDF)"
                  placeholder="Our own address"
                  value={inquiry.LeveringKlantID}
                  onChange={handleChange}
                  isDisabled={savingResult}
                />
                <FormLayout.Group>
                  <Select
                    id="TaalID"
                    maxLength={60}
                    autoComplete="off"
                    placeholder="Choose language"
                    label="Language"
                    value={parseInt(inquiry.TaalID) || ''}
                    onChange={handleChange}
                    disabled={savingResult}
                    options={languages
                      .sort((a, b) => alphabetize(a, b, 'Omschrijving'))
                      .map((lan) => {
                        return { value: lan.TaalID, label: lan.Omschrijving };
                      })}
                  />
                  <TextField
                    id="RefKlant"
                    maxLength={100}
                    showCharacterCount
                    type="text"
                    autoComplete="off"
                    label="Reference customer"
                    value={inquiry.RefKlant || ''}
                    onChange={handleChange}
                    disabled={savingResult}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    id="Leveringstermijn"
                    maxLength={50}
                    showCharacterCount
                    autoComplete="off"
                    label="Delivery term"
                    value={inquiry.Leveringstermijn || ''}
                    onChange={handleChange}
                    disabled={savingResult}
                  />
                  <TextField
                    id="RefKlantDatum"
                    maxLength={60}
                    type="date"
                    autoComplete="off"
                    label="Customer date"
                    value={String(inquiry.RefKlantDatum).substring(0, 10)}
                    onChange={handleChange}
                    disabled={savingResult}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <ProjectSelect value={inquiry.ProjectID} onChange={handleChange} id="ProjectID" label="Project" isDisabled={savingResult} />
                  <TextField
                    id="MachineType"
                    maxLength={50}
                    showCharacterCount
                    autoComplete="off"
                    label="Machine type"
                    value={inquiry.MachineType || ''}
                    onChange={handleChange}
                    disabled={savingResult}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <InlineGrid columns={2}>
                    <BlockStack>
                      <Checkbox id="Verstuurd" label="PDF document emailed" checked={inquiry.Verstuurd} onChange={handleChange} disabled={savingResult} />
                    </BlockStack>
                    <OwnerSelect id="GebruikerID" label="Owner" value={inquiry.GebruikerID} handleChange={handleChange} disabled={savingResult} />
                  </InlineGrid>
                  <TextField
                    id="MachineNummer"
                    maxLength={50}
                    showCharacterCount
                    autoComplete="off"
                    label="Machine number"
                    value={inquiry.MachineNummer || ''}
                    onChange={handleChange}
                    disabled={savingResult}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Form>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <LinesTotalCard summary="inquiry" lines={lines} />
            <MakePurchaseOrderModal
              open={orderModalOpen}
              loading={orderModalLoading}
              documents={uploadedDocuments}
              onClose={() => setOrderModalOpen(false)}
              onConfirm={(documentIds) => {
                setOrderModalOpen(false);
                InquiryToPurchase(inquiry, lines, company, lineInterface, navigate, documentIds);
              }}
            />
            <InquiryLifecycleCard
              inquiryId={id}
              inquiry={inquiry}
              company={company}
              statuses={inquiryStatuses}
              disabled={savingResult || documentInProgress}
              dirty={isDirty || linesDirty}
              onInquiryUpdated={handleInquiryUpdated}
              onMakePurchaseOrder={makePurchaseOrder}
            />
            <DocumentCard
              key={documentCardKey}
              resource="INQUIRY"
              id={id}
              title="Inquiry document"
              docNumber={inquiry.Prijsaanvraagnummer}
              isDirty={isDirty}
              linesDirty={linesDirty}
              generateDocument={generateDocument}
              docTypeID={docTypeID}
              docType={docType}
              company={company}
              setResource={handleInquiryUpdated}
              docLang={inquiry.TaalID}
              documentButtonLoading={savingResult || documentInProgress}
              setDocumentButtonLoading={setSavingResult}
              documentOptions={
                <Checkbox
                  label="Hide customer on PDF"
                  helpText="Leaves the end customer off the next generated PDF"
                  checked={hideCustomer}
                  onChange={(checked) => setHideCustomer(checked)}
                  disabled={savingResult || documentInProgress}
                />
              }
            />
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <Card>
            <CardHeading title="Content" />
            {partDocuments.modal}
            {partDocuments.banner ? <div style={{ marginBottom: '12px' }}>{partDocuments.banner}</div> : null}
            <LinesTable
              id={id}
              data={lines}
              updateMyData={updateMyData}
              setLines={setLines}
              setLinesDirty={setLinesDirty}
              onSubmit={saveForm}
              lineInterface={lineInterface}
              resource={inquiry}
              company={company}
              disabled={savingResult}
              setIsBlurred={setIsBlurred}
              onProductsAdded={partDocuments.promptForProducts}
            />
          </Card>
        </Layout.Section>
        <Layout.Section>
          <EventTimeLine resourceName="INQUIRY" resourceId={id} refreshToken={timelineRefresh} />
        </Layout.Section>
      </Layout>
    </Page>
  );
}
